import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { ArrowRight, ArrowLeft, Vote, HandHeart, Search, Users, MapPin, Eye, MessageCircle, Send, Facebook, Instagram, TrendingUp } from "lucide-react";
import { XIcon } from "@/components/icons/XIcon";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/i18n/I18nProvider";
import { Link, useNavigate } from "react-router-dom";
import { allSearchableParties, moroccoRegions } from "@/data/parties";
import { getTranslatedProvince } from "@/utils/translations";
import heroBg from "@/assets/hero-bg.jpg";
import GlobalAdBanner from "@/components/GlobalAdBanner";
import ElectionCountdown from "@/components/ElectionCountdown";
import HeroMiniCards from "@/components/HeroMiniCards";
import SpotNewsBanner from "@/components/SpotNewsBanner";
import { useActiveAds } from "@/hooks/useActiveAds";
import { isStagingModeEnabled } from "@/utils/stagingMode";
import { saveInteraction } from "@/data/interactionsStore";
import { useToast } from "@/hooks/use-toast";
import { getSectionContent } from "@/data/cmsStore";
import { getElectionResults } from "@/data/electionResultsStore";

const searchLabels = {
  fr: { title: "Elections parlementaires 2026", dataType: "Données", accreditation: "Accréditation candidats", results: "Résultats élections", party: "Parti politique", region: "Région", province: "Préfecture / Province", listType: "Type de liste", localList: "Liste locale", regionalList: "Liste régionale", search: "Rechercher", allParties: "Tous les partis", allRegions: "Toutes les régions", allProvinces: "Toutes les préfectures", allLists: "Tous les types" },
  ar: { title: "الانتخابات البرلمانية 2026", dataType: "البيانات", accreditation: "اعتماد المرشحين", results: "نتائج الانتخابات", party: "الحزب السياسي", region: "الجهة", province: "العمالة / الإقليم", listType: "نوع اللائحة", localList: "اللائحة المحلية", regionalList: "اللائحة الجهوية", search: "بحث", allParties: "جميع الأحزاب", allRegions: "جميع الجهات", allProvinces: "جميع العمالات والأقاليم", allLists: "جميع الأنواع" },
  zgh: { title: "ⵉⵙⵜⴰⵢⵏ ⵉⴱⵕⵍⴰⵎⴰⵏⵉⵢⵏ 2026", dataType: "ⵉⵙⴼⴽⴰ", accreditation: "ⴰⵙⵙⵉⴷⵖ ⵏ ⵉⵎⵙⵜⴰⵢⵏ", results: "ⵜⵉⵢⴰⴼⵓⵜⵉⵏ ⵏ ⵉⵙⵜⴰⵢⵏ", party: "ⴰⵎⵓⵔ ⴰⵙⵔⵜⴰⵏ", region: "ⵜⴰⵎⵏⴰⴹⵜ", province: "ⵜⴰⵎⵏⴰⴹⵜ", listType: "ⴰⵏⴰⵡ ⵏ ⵜⵍⴳⴰⵎⵜ", localList: "ⵜⴰⵍⴳⴰⵎⵜ ⵜⴰⴷⵖⴰⵔⴰⵏⵜ", regionalList: "ⵜⴰⵍⴳⴰⵎⵜ ⵜⴰⵎⵏⴰⴹⵜ", search: "ⵔⵣⵓ", allParties: "ⴰⴽⴽⵯ ⵉⵎⵓⵔⴰⵏ", allRegions: "ⴰⴽⴽⵯ ⵜⵉⵎⵏⴰⴹⵉⵏ", allProvinces: "ⴰⴽⴽⵯ ⵜⵉⵎⵏⴰⴹⵉⵏ", allLists: "ⴰⴽⴽⵯ ⴰⵏⴰⵡⵏ" }
};

const HeroSection = () => {
  const { t, lang, isRtl } = useI18n();
  const Arrow = isRtl ? ArrowLeft : ArrowRight;
  const l = searchLabels[lang as 'fr'|'ar'|'zgh'] || searchLabels.fr;
  const navigate = useNavigate();

  const [searchParty, setSearchParty] = useState("");
  const [isResultsActive, setIsResultsActive] = useState(false);
  const [searchRegion, setSearchRegion] = useState("");
  const [searchProvince, setSearchProvince] = useState("");
  const [searchList, setSearchList] = useState("");
  const [searchDataType, setSearchDataType] = useState("accreditation");
  const [messageCandidate, setMessageCandidate] = useState<{id: string, name: string, party: string} | null>(null);
  const { toast } = useToast();

  const searchProvinces = searchRegion && searchRegion !== "all"
    ? moroccoRegions.find(r => r.name === searchRegion)?.provinces || []
    : [];

  const [dynamicStats, setDynamicStats] = useState(t.hero.stats);
  const [cms, setCms] = useState(() => getSectionContent("hero", "home"));

  useEffect(() => {
    const handleCmsChange = () => setCms(getSectionContent("hero", "home"));
    const checkResults = () => {
      const res = getElectionResults();
      setIsResultsActive(!!res.config.isResultsNightActive);
    };
    checkResults();
    
    window.addEventListener("cmsLayoutChanged", handleCmsChange);
    window.addEventListener("cmsLayoutDraftChanged", handleCmsChange);
    window.addEventListener("stagingModeChanged", handleCmsChange);
    window.addEventListener("electionResultsUpdated", checkResults);
    
    return () => {
      window.removeEventListener("cmsLayoutChanged", handleCmsChange);
      window.removeEventListener("cmsLayoutDraftChanged", handleCmsChange);
      window.removeEventListener("stagingModeChanged", handleCmsChange);
      window.removeEventListener("electionResultsUpdated", checkResults);
    };
  }, []);

  useEffect(() => {
    const loadStats = () => {
      try {
        const stagingOn = isStagingModeEnabled();
        const savedDraft = localStorage.getItem("kaynin_hero_stats_draft");
        const savedLive = localStorage.getItem("kaynin_hero_stats");
        
        let statsToUse = savedLive;
        if (stagingOn && savedDraft) {
          statsToUse = savedDraft;
        }

        if (statsToUse) {
          const parsed = JSON.parse(statsToUse);
          if (parsed[lang]) {
            setDynamicStats(parsed[lang]);
            return;
          }
        }
      } catch (e) {
        // Fallback
      }
      setDynamicStats(t.hero.stats);
    };

    loadStats();
    window.addEventListener("kayninHeroStatsUpdated", loadStats);
    window.addEventListener("kayninHeroStatsDraftUpdated", loadStats);
    window.addEventListener("stagingModeChanged", loadStats);
    return () => {
      window.removeEventListener("kayninHeroStatsUpdated", loadStats);
      window.removeEventListener("kayninHeroStatsDraftUpdated", loadStats);
      window.removeEventListener("stagingModeChanged", loadStats);
    };
  }, [lang, t.hero.stats]);

  const isOnlyRegionSelected = searchRegion && searchRegion !== "all" && (!searchParty || searchParty === "all") && (!searchProvince || searchProvince === "all") && (!searchList || searchList === "all");

  return (
    <section className="relative min-h-[100svh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={cms?.hero_bg_image || heroBg} alt="Citoyens marocains participant à la démocratie" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-hero opacity-85" />
        <div className="absolute inset-0 geometric-pattern" />
      </div>

      <div className="container relative z-10 pt-16 pb-12 md:pt-20 md:pb-14">
        <div className="flex flex-col lg:flex-row items-start justify-between gap-8 lg:gap-12">
         <div className="max-w-3xl flex-1 w-full">
          <div className="flex flex-col md:flex-row items-start gap-4 mb-4 md:mb-6">
            <motion.h1
              className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-display font-bold text-primary-foreground leading-tight m-0 text-start"
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            >
              {(() => {
                const titleStr = cms[`hero_title_${lang}`];
                if (titleStr) {
                  const highlights = ["L'espace citoyen", "الفضاء المواطن", "فضاءك المواطن", "ⴰⵙⴰⵢⵔⴰⵔ ⴰⵏⴰⵎⵓⵔ", "espace citoyen"];
                  for (const h of highlights) {
                    if (titleStr.includes(h)) {
                      const parts = titleStr.split(h);
                      return <>{parts[0]}<span className="text-accent">{h}</span>{parts.slice(1).join(h)}</>;
                    }
                  }
                  return titleStr;
                }
                return (
                  <>
                    {t.hero.title1}
                    <span className="text-accent">{t.hero.titleHighlight}</span>
                    {t.hero.title2}
                  </>
                );
              })()}
            </motion.h1>
          </div>

          <motion.p
            className="text-base md:text-xl text-primary-foreground/80 font-body mb-6 md:mb-10 max-w-2xl leading-relaxed text-start"
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
          >
            {cms[`hero_subtitle_${lang}`] || t.hero.subtitle}
          </motion.p>

          <motion.div className="block lg:hidden w-full mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.35 }}>
            <HeroMiniCards />
          </motion.div>

          <motion.div
            className="flex flex-col gap-4"
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.45 }}
          >
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 flex-wrap w-full">
              <Button size="lg" className="w-full sm:w-auto text-base sm:text-lg px-6 py-5 sm:px-8 sm:py-6 font-body font-semibold bg-primary-foreground text-foreground hover:bg-primary-foreground/90 disabled:opacity-50" asChild>
                <Link to="/voter" className="flex items-center justify-center gap-2">
                  <Vote className="w-5 h-5 shrink-0" />
                  <span className="truncate">{t.hero.voteBtn}</span>
                  <Arrow className="w-4 h-4 shrink-0" />
                </Link>
              </Button>
              <Button size="lg" variant="secondary" className="w-full sm:w-auto text-base sm:text-lg px-6 py-5 sm:px-8 sm:py-6 font-body font-semibold disabled:opacity-50" asChild>
                <Link to="/engagement" className="flex items-center justify-center gap-2">
                  <HandHeart className="w-5 h-5 shrink-0" />
                  <span className="truncate">{t.hero.engageBtn}</span>
                  <Arrow className="w-4 h-4 shrink-0" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="w-full sm:w-auto text-base sm:text-lg px-6 py-5 sm:px-8 sm:py-6 font-body font-semibold border-white/30 text-white bg-white/10 hover:bg-white/20 disabled:opacity-50" asChild>
                <Link to="/observateur" className="flex items-center justify-center gap-2">
                  <Eye className="w-5 h-5 shrink-0" />
                  <span className="truncate">{(t.hero as { observeBtn?: string }).observeBtn || "Je m'engage"}</span>
                  <Arrow className="w-4 h-4 shrink-0" />
                </Link>
              </Button>
              <Button size="lg" className="w-full sm:w-auto text-base sm:text-lg px-6 py-5 sm:px-8 sm:py-6 font-body font-semibold bg-accent text-accent-foreground hover:bg-accent/90 disabled:opacity-50 shadow-lg shadow-accent/20" asChild>
                <Link to="/candidat-info" className="flex items-center justify-center gap-2">
                  <Users className="w-5 h-5 shrink-0" />
                  <span className="truncate">{(t.hero as { candidatBtn?: string }).candidatBtn || "Je veux être candidat"}</span>
                  <Arrow className="w-4 h-4 shrink-0" />
                </Link>
              </Button>
            </div>
            <ElectionCountdown />
          </motion.div>
         </div>
         {/* Banner Ad placement and MiniCards (Right of text) — hidden on mobile */}
         <motion.div 
            className="hidden lg:flex flex-col gap-6 w-full lg:w-80 shrink-0 mt-8 lg:mt-12"
            initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
         >
            <GlobalAdBanner />
            <HeroMiniCards />
         </motion.div>
        </div>

        {/* Candidates Search Bar */}
        {(cms?.hero_hide_search_bar !== 'true' && cms?.hero_hide_search_bar !== true) && (
          <motion.div
            id="hero-search"
            className="mt-12 w-full max-w-4xl relative scroll-mt-24 flex flex-col"
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.55 }}
          >
           <SpotNewsBanner />
           <div className="bg-card/95 backdrop-blur-md p-6 rounded-3xl md:rounded-t-none border border-border shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary z-10" />
          <h3 className="text-foreground font-display font-bold text-lg md:text-xl mb-5 flex items-center gap-2">
            <Search className="w-5 h-5 text-primary" />
            {l.title}
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <Select onValueChange={setSearchDataType} value={searchDataType} disabled={cms?.hero_disable_search === 'true' || cms?.hero_disable_search === true}>
                <SelectTrigger className="w-full bg-background border-border/60 hover:border-primary/50 transition-colors h-12 rounded-xl text-sm font-body shadow-sm">
                  <SelectValue placeholder={l.dataType} />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="accreditation">{l.accreditation}</SelectItem>
                  <SelectItem value="results">{l.results}</SelectItem>
                </SelectContent>
              </Select>
              
              <Select onValueChange={setSearchParty} value={searchParty} disabled={cms?.hero_disable_search === 'true' || cms?.hero_disable_search === true}>
                <SelectTrigger className="w-full bg-background border-border/60 hover:border-primary/50 transition-colors h-12 rounded-xl text-sm font-body shadow-sm">
                  <SelectValue placeholder={l.party} />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all">{l.allParties}</SelectItem>
                  {allSearchableParties.map(p => {
                    const partyName = t.parties?.partiesNames ? t.parties.partiesNames[p.acronym] : undefined;
                    const arabicName = (p as { acronymAr?: string }).acronymAr;
                    const display = partyName || (lang === 'ar' && arabicName ? `${arabicName} (${p.acronym})` : p.acronym);
                    return <SelectItem key={p.acronym} value={p.acronym}>{display}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              
              <Select onValueChange={(val) => { setSearchRegion(val); setSearchProvince(""); }} value={searchRegion} disabled={cms?.hero_disable_search === 'true' || cms?.hero_disable_search === true}>
                <SelectTrigger className="w-full bg-background border-border/60 hover:border-primary/50 transition-colors h-12 rounded-xl text-sm font-body shadow-sm">
                  <SelectValue placeholder={l.region} />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all">{l.allRegions}</SelectItem>
                  {moroccoRegions.map(r => (
                    <SelectItem key={r.name} value={r.name}>{lang === 'ar' && r.nameAr ? r.nameAr : r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select onValueChange={setSearchProvince} value={searchProvince} disabled={!searchRegion || searchRegion === 'all' || cms?.hero_disable_search === 'true' || cms?.hero_disable_search === true}>
                <SelectTrigger className="w-full bg-background border-border/60 hover:border-primary/50 transition-colors h-12 rounded-xl text-sm font-body shadow-sm disabled:opacity-50">
                  <SelectValue placeholder={l.province} />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all">{l.allProvinces}</SelectItem>
                  {searchProvinces.map(p => (
                    <SelectItem key={p} value={p}>{getTranslatedProvince(p, lang)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select onValueChange={setSearchList} value={searchList} disabled={cms?.hero_disable_search === 'true' || cms?.hero_disable_search === true}>
                <SelectTrigger className="w-full bg-background border-border/60 hover:border-primary/50 transition-colors h-12 rounded-xl text-sm font-body shadow-sm">
                  <SelectValue placeholder={l.listType} />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all">{l.allLists}</SelectItem>
                  <SelectItem value="locale">{l.localList}</SelectItem>
                  <SelectItem value="regionale">{l.regionalList}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex flex-col md:flex-row items-center gap-4 mt-6">
              <Button 
                disabled={cms?.hero_disable_search === 'true' || cms?.hero_disable_search === true}
                className="w-full md:w-auto md:min-w-[240px] bg-primary text-primary-foreground font-body font-semibold py-6 rounded-xl hover:bg-primary/90 hover:scale-[1.02] transition-all shadow-md active:scale-95 group disabled:opacity-50 disabled:hover:scale-100"
                onClick={() => {
                  let query = `?region=${encodeURIComponent(searchRegion !== 'all' ? searchRegion : '')}`;
                  if (searchParty && searchParty !== "all") query += `&party=${encodeURIComponent(searchParty)}`;
                  if (searchProvince && searchProvince !== "all") query += `&province=${encodeURIComponent(searchProvince)}`;
                  if (searchList && searchList !== "all") query += `&type=${encodeURIComponent(searchList)}`;
                  if (searchDataType && searchDataType !== "accreditation") query += `&dataType=${encodeURIComponent(searchDataType)}`;
                  navigate(`/resultats-candidats${query}`);
                }}
              >
                <Search className="w-5 h-5 mr-0 md:mr-2 rtl:mr-0 rtl:ml-2 rtl:md:mr-0 md:rtl:ml-2 group-hover:scale-110 transition-transform" />
                {l.search}
              </Button>
              
              {(cms?.hero_disable_search === 'true' || cms?.hero_disable_search === true) && (
                <span className="text-secondary font-display font-extrabold text-sm md:text-xl animate-pulse text-center w-full md:w-auto mt-2 md:mt-0">
                  {lang === 'ar' ? 'قيد التجميع...' : lang === 'zgh' ? 'ⴰⵔ ⵉⵜⵜⵓⵙⵎⵓⵏ...' : 'En cours de consolidation...'}
                </span>
              )}
            </div>
            
            {/* Modal de Contact Citoyen-Candidat (séparé du Search Dialog pour z-index) */}
            {messageCandidate && (
              <Dialog open={!!messageCandidate} onOpenChange={(open) => !open && setMessageCandidate(null)}>
                <DialogContent className="max-w-2xl w-[95vw] rounded-3xl p-0 shadow-2xl border-none overflow-hidden" style={{ zIndex: 100 }}>
                  <div className="h-2 w-full bg-primary" />
                  <DialogHeader className="px-6 pt-6 pb-2 text-left">
                    <DialogTitle className="font-display text-2xl flex items-center gap-2 mb-2 font-bold text-primary">
                      <MessageCircle className="w-6 h-6" />
                      Contacter {messageCandidate.name}
                    </DialogTitle>
                    <DialogDescription className="font-body text-muted-foreground">
                      Envoyez un message direct à ce candidat. Vos coordonnées lui seront transmises.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const fd = new FormData(e.currentTarget);
                    saveInteraction({
                      type: "candidate",
                      candidateId: messageCandidate.id,
                      candidateName: messageCandidate.name,
                      partyAcronym: messageCandidate.party,
                      senderLastName: fd.get("lastName") as string,
                      senderFirstName: fd.get("firstName") as string,
                      senderEmail: fd.get("email") as string,
                      senderPhone: fd.get("phone") as string,
                      region: searchRegion && searchRegion !== "all" ? searchRegion : "Inconnue",
                      province: searchProvince && searchProvince !== "all" ? searchProvince : "Inconnue",
                      subject: fd.get("subject") as string,
                      message: fd.get("message") as string,
                    });
                    toast({
                      title: "Message envoyé",
                      description: `Votre message a bien été envoyé à ${messageCandidate.name}.`,
                    });
                    setMessageCandidate(null);
                  }} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar bg-card text-left">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-body font-medium mb-1.5 text-foreground">Nom *</label>
                        <input required name="lastName" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus:ring-1 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-body font-medium mb-1.5 text-foreground">Prénom *</label>
                        <input required name="firstName" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus:ring-1 focus:ring-primary" />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-body font-medium mb-1.5 text-foreground">Téléphone *</label>
                        <input required name="phone" type="tel" pattern="^[+]?[0-9]{10,}$" title="num incorrect" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus:ring-1 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-body font-medium mb-1.5 text-foreground">Email *</label>
                        <input required name="email" type="email" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus:ring-1 focus:ring-primary" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-body font-medium mb-1.5 text-foreground">Objet *</label>
                      <input required name="subject" className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-body font-medium mb-1.5 text-foreground">Message *</label>
                      <textarea required name="message" className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                    <Button type="submit" className="w-full gap-2 font-body font-semibold text-lg py-6 mt-4 text-white shadow-md border-none bg-primary hover:bg-primary/90">
                      <Send className="w-5 h-5" /> Envoyer
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
           </div>
          </motion.div>
        )}

        <motion.div
          className="mt-10 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6"
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.6 }}
        >
          {dynamicStats.map((stat, i) => (
            <div key={i} className="text-center p-3 md:p-4 rounded-lg bg-primary-foreground/10 backdrop-blur-sm border border-primary-foreground/10">
              <div className="text-xl md:text-3xl font-display font-bold text-accent">{stat.value}</div>
              <div className="text-xs md:text-sm text-primary-foreground/70 font-body mt-1">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
